import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import { LinkButton } from '@atlaskit/button/new';

import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import config from 'appconfig';

import MACPageHeader from './components/macPageHeader';
import { State } from '../../model/State';
import { selectLicenseDetails } from './duck/selectors/license';
import { messages } from './messages';
import { Dimensions, Font } from '../../components/visuals';
import { PageH3Header } from './components/styledComponents';
import { selectDetails, selectSingleOrder } from './duck/selectors';
import { fetchDetails } from './duck/actions/accounts';
import { fetchLicenseById } from './duck/actions/license';
import { isAddOnType } from '../../util/mac/helpers';
import Spinner from '@atlaskit/spinner';

const SectionTitle = styled.h3`
  color: ${token('color.text', '#172B4D')};
  font-size: ${Dimensions.units(2.5)}px;
  margin-top: ${Dimensions.units(3.75)}px !important;
  margin-bottom: ${Dimensions.units(2)}px;
  font-weight: 500;
  font-family: ${Font.displayFamily};
  letter-spacing: 0.3px;
`;

const DownloadButton = styled(LinkButton)`
  margin-top: ${Dimensions.units(1)}px;
`;

const List = styled.ul`
  padding-left: ${Dimensions.units(2.5)}px;
`;
const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const StyledParagraph = styled.p<{ $marginTop?: number }>`
  margin-top: ${({ $marginTop }) => $marginTop && `${Dimensions.units($marginTop)}px`};
`;

const Loading = () => (
  <SpinnerWrapper>
    <Spinner size="large" />
  </SpinnerWrapper>
);

const getLink = (productKeyWithoutPrefix: string, isDataCenter: boolean): string => {
  let link: string;
  if (isDataCenter) {
    const trimmedKey = productKeyWithoutPrefix.substring(
      0,
      productKeyWithoutPrefix.length - '.data-center'.length,
    );
    link = `${config.marketplaceUrl}/download/plugins/${trimmedKey}?hosting=datacenter`;
  } else {
    link = `${config.marketplaceUrl}/download/plugins/${productKeyWithoutPrefix}?hosting=server`;
  }
  return link;
};

const AddonLicenseViewPage = () => {
  const dispatch = useDispatch();
  const orderId = useSelector((state) => (state as State).location.query?.orderId);
  const singleOrder = useSelector((state) => selectSingleOrder(Number(orderId), state as State));
  const accountId = singleOrder?.cartOrderDetails?.items[0]?.accountId;
  const accountDetails = useSelector((state: State) => selectDetails(state, accountId));

  const licenseId = useSelector((state) => {
    const { payload, query } = (state as State).location;
    return payload.licenseId || query?.licenseId || query?.license;
  });
  const licenseIdFromAccount = accountDetails?.latestPeriod?.license?.licenseDetails?.id;
  const licenseDetails = useSelector((state) =>
    selectLicenseDetails(state as State, licenseId || licenseIdFromAccount),
  );
  const addOn = licenseDetails?.productPackageDetails;
  const productKey = addOn?.key?.replace('pricingplan.', '');
  const link = getLink(productKey, addOn?.isDataCenter);

  useEffect(() => {
    if (singleOrder && singleOrder?.cartOrderDetails?.items.length === 1) {
      dispatch(fetchDetails(accountId));
    }
  }, [singleOrder]);

  useEffect(() => {
    if (
      accountDetails &&
      isAddOnType(accountDetails?.latestPeriod?.license?.productPackageDetails?.productType)
    ) {
      dispatch(fetchLicenseById(licenseIdFromAccount));
    }
  }, [accountDetails]);

  return !licenseDetails ? (
    <Loading />
  ) : (
    <>
      <MACPageHeader />
      <PageH3Header>
        <FormattedMessage {...messages.addonLicenseViewPageTitle} />
      </PageH3Header>
      <hr />

      <SectionTitle>
        <FormattedMessage {...messages.addonLicenseKeyInfoTitle} />
      </SectionTitle>
      <TextArea
        id="area"
        maxHeight="30vh"
        name="area"
        value={licenseDetails?.licenseDetails?.license}
        onClick={(e) => {
          e.currentTarget.select();
        }}
      />
      <SectionTitle>
        <FormattedMessage {...messages.addonDownloadAppTitle} />
      </SectionTitle>

      <p>
        <FormattedMessage
          id="mac.license.addon.view.download.app.explanation"
          defaultMessage="Download the {description} app to your local system:"
          values={{
            description: <em>{licenseDetails?.productPackageDetails?.description}</em>,
          }}
        />
      </p>

      <DownloadButton href={link} appearance="primary">
        <FormattedMessage {...messages.addonDownloadButtonText} />
      </DownloadButton>

      <SectionTitle>
        <FormattedMessage {...messages.addonInstallAppTitle} />
      </SectionTitle>
      <p>
        <FormattedMessage
          id="mac.license.addon.view.install.app.note"
          defaultMessage="{note} You must have administrative rights to install an app."
          values={{
            note: (
              <strong>
                <FormattedMessage {...messages.addonNoteText} />
              </strong>
            ),
          }}
        />
      </p>

      <StyledParagraph $marginTop={1}>
        <List>
          <li>
            <FormattedMessage {...messages.addonInstallOpen} />
          </li>
          <li>
            <FormattedMessage {...messages.addonInstallNavigate} />
          </li>
          <li>
            <FormattedMessage {...messages.addonInstallChose} />
          </li>
          <li>
            <FormattedMessage {...messages.addonInstallUpload} />
          </li>
        </List>
      </StyledParagraph>

      <SectionTitle>
        <FormattedMessage {...messages.addonApplyLicenseTitle} />
      </SectionTitle>

      <p>
        <List>
          <li>
            <FormattedMessage {...messages.addonLicensesNavigate} />
          </li>
          <li>
            <FormattedMessage
              id="mac.license.addon.view.licenses.locate"
              defaultMessage="Locate your installed {productDescription} app in the list."
              values={{
                productDescription: <em>{addOn?.description}</em>,
              }}
            />
          </li>

          <li>
            <FormattedMessage {...messages.addonLicensesCopy} />
          </li>
        </List>
      </p>
      <StyledParagraph $marginTop={4}>
        <a
          href="https://confluence.atlassian.com/x/eJgCDQ"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FormattedMessage {...messages.addonLicenseLearnMore} />
        </a>
      </StyledParagraph>
    </>
  );
};

export const MACAddonLicenseViewPage = connect(null, null)(AddonLicenseViewPage);
